import { useState, useEffect, useCallback } from "react";
import { FiSearch, FiChevronRight, FiArrowLeft, FiArrowUp, FiArrowDown, FiSliders, FiX } from "react-icons/fi";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";
import { PageHeader, Card, Pill, Btn, THead, TD, Icon, Pagination, PER_PAGE_DEFAULT } from "./KioskUI";
import BulkImport from "./BulkImport";

const PRODUCT_IMPORT_COLUMNS = [
  { key: "name", label: "Name", required: true },
  { key: "category", label: "Category" },
  { key: "price", label: "Price" },
  { key: "low_stock_threshold", label: "Low Stock Threshold" },
];

/* ── helpers ── */
const MMK = (n) => Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });

function stockStatus(p) {
  if (p.quantity === 0) return "out";
  if (p.quantity <= p.low_stock_threshold) return "low";
  return "ok";
}

const STATUS_LABEL = { ok: "In stock", low: "Low stock", out: "Out of stock" };

/* ── movement type config ── */
const MOVE_TYPE = {
  SALE:       { icon: FiArrowUp,   bg: "var(--blue-50)",    fg: "var(--primary)", label: "Sold"     },
  RESTOCK:    { icon: FiArrowDown, bg: "var(--success-bg)", fg: "var(--success)", label: "Stock in"  },
  ADJUSTMENT: { icon: FiSliders,   bg: "var(--warning-bg)", fg: "var(--warning)", label: "Adjusted"  },
};

/* ── Movement history ── */
export function MovementHistory({ productId, token }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet(`/api/inventory/products/${productId}/movements`, token)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [productId, token]);

  const movements = data?.movements || [];

  return (
    <Card style={{ overflow: "hidden", marginTop: 16 }}>
      <div style={{ padding: "15px 20px", borderBottom: "1px solid var(--hair)", display: "flex", alignItems: "baseline", gap: 10 }}>
        <div style={{ fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>Movement history</div>
        <span style={{ fontFamily: "var(--font-mm)", fontSize: 13.5, color: "var(--n-400)" }}>စာရင်းလှုပ်ရှားမှု</span>
        <span style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--sub)" }}>{movements.length} entries</span>
      </div>

      {loading ? (
        <div style={{ padding: "28px 20px", textAlign: "center", color: "var(--sub)", fontSize: 13.5 }}>Loading…</div>
      ) : movements.length === 0 ? (
        <div style={{ padding: "30px 20px", textAlign: "center", color: "var(--n-400)", fontSize: 13.5 }}>No movements recorded yet.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <THead cols={[{ t: "Type" }, { t: "Date" }, { t: "Party / reason" }, { t: "Change", r: 1 }]} />
          <tbody>
            {movements.map((m, i) => {
              const cfg = MOVE_TYPE[m.movement_type] || MOVE_TYPE.ADJUSTMENT;
              const Ico = cfg.icon;
              return (
                <tr key={i}>
                  <td style={{ padding: "12px 16px", borderTop: "1px solid var(--hair)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 30, height: 30, borderRadius: 9, background: cfg.bg, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Ico size={15} color={cfg.fg} />
                      </span>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ink)" }}>{cfg.label}</span>
                    </div>
                  </td>
                  <TD mut>{new Date(m.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</TD>
                  <TD>{m.customer_name || m.note || "—"}</TD>
                  <td style={{ padding: "12px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                    <span style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: m.quantity > 0 ? "var(--success)" : "var(--danger)" }}>
                      {m.quantity > 0 ? "+" : ""}{m.quantity}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ── Product detail / form ── */
function ProductDetail({ productId, isNew, categories, suppliers, token, go, onBack, onSaved, onDeleted }) {
  const [form, setForm]     = useState({ name: "", category: "", supplier: "", price: "", low_stock_threshold: "5" });
  const [product, setProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => {
    if (isNew) return;
    apiGet(`/api/inventory/products/${productId}`, token)
      .then(r => r.json())
      .then(p => {
        setProduct(p);
        setForm({ name: p.name, category: String(p.category), supplier: p.supplier ? String(p.supplier) : "", price: String(p.price), low_stock_threshold: String(p.low_stock_threshold) });
      })
      .catch(console.error);
  }, [productId, isNew, token]);

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      const payload = { name: form.name, category: form.category, supplier: form.supplier || null, price: form.price, low_stock_threshold: form.low_stock_threshold };
      const res = isNew
        ? await apiPost("/api/inventory/products", payload, token)
        : await apiPut(`/api/inventory/products/${productId}`, payload, token);
      if (res.ok) {
        const data = await res.json();
        onSaved(data, isNew);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || err.detail || "Failed to save product.");
      }
    } catch { setError("Cannot connect to server."); }
    finally { setSaving(false); }
  };

  const [deleteError, setDeleteError] = useState("");

  const handleDelete = async () => {
    setSaving(true); setDeleteError("");
    try {
      const res = await apiDelete(`/api/inventory/products/${productId}`, token);
      if (res.ok) { onDeleted(productId); }
      else {
        const err = await res.json().catch(() => ({}));
        setDeleteError(err.error || "Failed to delete.");
        // keep modal open so user sees the reason
      }
    } catch { setDeleteError("Cannot connect to server."); }
    finally { setSaving(false); }
  };

  const p = product;
  const st = p ? stockStatus(p) : "ok";
  const loading = !isNew && !product;

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 240 }}>
      <span style={{ color: "var(--sub)", fontSize: 14 }}>Loading…</span>
    </div>
  );

  return (
    <div>
      {/* Back + title bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <button onClick={onBack} style={{ border: "1px solid var(--line)", background: "var(--surface)", borderRadius: "var(--r-sm)", padding: 8, cursor: "pointer", display: "flex" }}>
          <FiArrowLeft size={18} color="var(--sub)" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 25, fontWeight: 800, letterSpacing: "-.025em", margin: 0, color: "var(--ink)" }}>
            {isNew ? "New Product" : (p?.name || "Product")}
          </h1>
          <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 3 }}>
            {isNew ? "Add a product to the catalogue" : `${p?.sku || "—"} · ${p?.category_name || "—"}${p?.supplier_name ? ` · ${p.supplier_name}` : ""}`}
          </div>
        </div>
        {!isNew && <Pill tone={st}>{STATUS_LABEL[st]}</Pill>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16, alignItems: "start" }}>
        {/* Left: product details */}
        <Card style={{ padding: 24 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 18 }}>Product details</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <FormField label="Product name" mm="အမည်" style={{ gridColumn: "1 / -1" }}>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Cotton Tee — White" style={inputStyle} />
            </FormField>

            {!isNew && (
              <FormField label="SKU">
                <input value={p?.sku || "—"} readOnly style={{ ...inputStyle, color: "var(--sub)" }} />
              </FormField>
            )}

            <FormField label="Category" mm="အမျိုးအစား">
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="">Choose…</option>
                {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
              </select>
            </FormField>

            <FormField label="Supplier" mm="ပေးသွင်းသူ">
              <select value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))}
                style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="">None</option>
                {suppliers.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </select>
            </FormField>

            <FormField label="Sell price" mm="ရောင်းဈေး" unit="MMK">
              <input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                placeholder="0" style={{ ...inputStyle, textAlign: "right" }} />
            </FormField>

            {!isNew && (
              <FormField label="Current stock" unit="pcs">
                <input value={p?.quantity ?? ""} readOnly style={{ ...inputStyle, textAlign: "right", color: "var(--sub)" }} />
              </FormField>
            )}

            <FormField label="Reorder level" unit="pcs">
              <input type="number" value={form.low_stock_threshold} onChange={e => setForm(f => ({ ...f, low_stock_threshold: e.target.value }))}
                placeholder="0" style={{ ...inputStyle, textAlign: "right" }} />
            </FormField>
          </div>

          {/* Cost note */}
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 18, padding: "11px 13px", background: "var(--faint)", borderRadius: "var(--r-sm)" }}>
            <Icon name="restock" size={17} stroke="var(--n-400)" />
            <span style={{ fontSize: 12.5, color: "var(--sub)", lineHeight: 1.5 }}>
              Cost is set when you{" "}
              <button onClick={() => go("restock")} style={{ border: "none", background: "transparent", color: "var(--primary)", fontWeight: 700, cursor: "pointer", padding: 0, fontSize: 12.5, fontFamily: "var(--font-body)" }}>
                receive stock
              </button>{" "}
              — it's recorded against each restock, not edited here.
            </span>
          </div>

          {error && (
            <div style={{ marginTop: 12, fontSize: 13, color: "var(--danger)", padding: "8px 12px", background: "var(--danger-bg)", borderRadius: "var(--r-sm)" }}>
              {error}
            </div>
          )}
        </Card>

        {/* Right: actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Stock warning */}
          {!isNew && st !== "ok" && (
            <Card style={{ padding: "18px 20px", background: st === "out" ? "var(--danger-bg)" : "var(--warning-bg)", borderColor: "transparent" }}>
              <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                <Icon name="alert" size={20} stroke={st === "out" ? "var(--danger)" : "var(--warning)"} />
                <div style={{ fontSize: 13.5, color: st === "out" ? "var(--danger)" : "var(--warning)", fontWeight: 600, lineHeight: 1.5 }}>
                  {st === "out" ? "This product is out of stock." : `Below reorder level (${p?.quantity} of ${p?.low_stock_threshold}).`}
                </div>
              </div>
            </Card>
          )}

          {/* Cancel / Save */}
          <div style={{ display: "flex", gap: 11 }}>
            <Btn kind="ghost" onClick={onBack} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
            <Btn kind="pri" icon="check" onClick={handleSave} disabled={saving || !form.name.trim()} style={{ flex: 1, justifyContent: "center" }}>
              {saving ? "Saving…" : "Save"}
            </Btn>
          </div>

          {!isNew && (
            <Btn kind="soft" icon="restock" onClick={() => go("restock")} style={{ justifyContent: "center" }}>
              Restock this product
            </Btn>
          )}

          {!isNew && (
            <Btn kind="danger" icon="trash" onClick={() => setDelConfirm(true)} style={{ justifyContent: "center" }}>
              Delete product
            </Btn>
          )}
        </div>
      </div>

      {/* Movement history */}
      {!isNew && <MovementHistory productId={productId} token={token} />}

      {/* Delete confirm modal */}
      {delConfirm && (
        <div onClick={() => { setDelConfirm(false); setDeleteError(""); }} style={{ position: "fixed", inset: 0, background: "rgba(19,24,32,.42)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: "var(--r-xl)", boxShadow: "0 24px 70px rgba(19,24,32,.3)", width: 420, padding: "26px 26px 22px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 13, marginBottom: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: "var(--danger-bg)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="trash" size={20} stroke="var(--danger)" />
              </div>
              <div>
                <div style={{ fontFamily: "var(--font-head)", fontSize: 17, fontWeight: 800, color: "var(--ink)" }}>Delete "{p?.name}"?</div>
                <div style={{ fontSize: 13.5, color: "var(--sub)", marginTop: 4, lineHeight: 1.5 }}>
                  This removes the product from the catalogue.
                  <br />
                  <span style={{ color: "var(--warning)", fontWeight: 600 }}>⚠ Products with sales or restock history cannot be deleted</span> — those records would be broken.
                </div>
              </div>
            </div>
            {deleteError && (
              <div style={{ fontSize: 13.5, color: "var(--danger)", background: "var(--danger-bg)", borderRadius: "var(--r-sm)", padding: "10px 14px", marginBottom: 14, lineHeight: 1.5 }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <Btn kind="ghost" onClick={() => { setDelConfirm(false); setDeleteError(""); }} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
              <button onClick={handleDelete} disabled={saving}
                style={{ flex: 1, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "var(--font-head)", fontWeight: 700, fontSize: 14.5, borderRadius: "var(--r-sm)", padding: "11px 18px", border: "none", cursor: saving ? "not-allowed" : "pointer", background: "var(--danger)", color: "#fff", opacity: saving ? 0.5 : 1 }}>
                <Icon name="trash" size={16} stroke="#fff" /> Delete product
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, mm, unit, style, children }) {
  return (
    <div style={style}>
      <label style={{ fontSize: 12.5, fontWeight: 700, color: "var(--sub)", letterSpacing: ".02em", display: "flex", gap: 7, alignItems: "baseline", marginBottom: 7 }}>
        {label}
        {mm && <span style={{ fontFamily: "var(--font-mm)", fontWeight: 500, color: "var(--n-400)" }}>{mm}</span>}
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "11px 13px", background: "var(--surface)" }}>
        {children}
        {unit && <span style={{ fontSize: 13, color: "var(--n-400)", fontWeight: 600, whiteSpace: "nowrap" }}>{unit}</span>}
      </div>
    </div>
  );
}

const inputStyle = {
  border: "none", outline: "none", background: "transparent",
  width: "100%", fontFamily: "var(--font-body)", fontSize: 14.5, color: "var(--ink)",
};

/* ── Products list ── */
function ProductList({ products, categories, loading, role, token, onSelect, onAddNew, onImported }) {
  const [q, setQ]     = useState("");
  const [cat, setCat] = useState("all");
  const [page, setPage] = useState(1);
  const [importing, setImporting] = useState(false);
  const isOwner = ["OWNER", "MANAGER"].includes(role);

  const qq = q.trim().toLowerCase();
  const filtered = products.filter(p =>
    (cat === "all" || String(p.category) === cat) &&
    (!qq || p.name.toLowerCase().includes(qq) || (p.sku || "").toLowerCase().includes(qq))
  );
  const list = filtered.slice((page - 1) * PER_PAGE_DEFAULT, page * PER_PAGE_DEFAULT);

  const cols = [
    { t: "Product" }, { t: "SKU" }, { t: "Category" },
    ...(isOwner ? [] : []),
    { t: "Price", r: 1 }, { t: "Stock", r: 1 }, { t: "Status", r: 1 }, { t: "", r: 1, w: 40 },
  ];

  return (
    <div>
      <PageHeader
        title="Products"
        mm="ကုန်ပစ္စည်း"
        desc={`${products.length} item${products.length !== 1 ? "s" : ""} · ${categories.length} categor${categories.length !== 1 ? "ies" : "y"}`}
      >
        {/* Search */}
        <div style={{ display: "flex", alignItems: "center", gap: 9, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-sm)", padding: "9px 13px", width: 280 }}>
          <FiSearch size={16} color="var(--n-400)" style={{ flexShrink: 0 }} />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(1); }} placeholder="Search products…"
            style={{ border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--ink)", width: "100%" }} />
          {q && <button onClick={() => setQ("")} style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", padding: 0 }}><FiX size={14} color="var(--n-400)" /></button>}
        </div>
        <Btn kind="ghost" onClick={() => setImporting(true)}>Import</Btn>
        <Btn kind="pri" icon="plus" onClick={onAddNew}>Add Product</Btn>
      </PageHeader>

      {importing && (
        <BulkImport
          title="Products"
          columns={PRODUCT_IMPORT_COLUMNS}
          endpoint="/api/inventory/products/bulk"
          token={token}
          onClose={() => setImporting(false)}
          onDone={onImported}
        />
      )}

      {/* Category filter chips */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {[{ id: "all", name: "All" }, ...categories].map(c => {
          const on = cat === String(c.id);
          return (
            <button key={c.id} onClick={() => { setCat(String(c.id)); setPage(1); }}
              style={{ padding: "7px 14px", borderRadius: 999, cursor: "pointer", border: `1px solid ${on ? "var(--primary)" : "var(--line)"}`, background: on ? "var(--primary)" : "var(--surface)", color: on ? "#fff" : "var(--n-600)", fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, transition: "background .1s" }}>
              {c.name}
            </button>
          );
        })}
      </div>

      <Card style={{ overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "48px 20px", textAlign: "center", color: "var(--sub)", fontSize: 14 }}>Loading…</div>
        ) : (
          <>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <THead cols={cols} />
            <tbody>
              {list.length === 0 && (
                <tr>
                  <td colSpan={cols.length} style={{ padding: "34px 16px", textAlign: "center", color: "var(--n-400)", fontSize: 14 }}>
                    {qq ? `No products match "${q}".` : "No products yet."}
                  </td>
                </tr>
              )}
              {list.map(p => {
                const st = stockStatus(p);
                return (
                  <tr key={p.id} style={{ cursor: "pointer" }}
                    onClick={() => onSelect(p.id)}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--faint)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <TD b>{p.name}</TD>
                    <TD mut>{p.sku || "—"}</TD>
                    <TD mut>{p.category_name || "—"}</TD>
                    <TD r b>{MMK(p.price)}</TD>
                    <TD r>{p.quantity}</TD>
                    <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                      <Pill tone={st}>{STATUS_LABEL[st]}</Pill>
                    </td>
                    <td style={{ padding: "14px 16px", borderTop: "1px solid var(--hair)", textAlign: "right" }}>
                      <FiChevronRight size={17} color="var(--n-300)" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination total={filtered.length} page={page} onChange={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}

/* ── Root ── */
export default function Products({ token, role, go }) {
  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [view, setView]             = useState("list"); // "list" | "detail"
  const [selectedId, setSelectedId] = useState(null);  // productId | "__new__"

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, cr, sr] = await Promise.all([
        apiGet("/api/inventory/products", token).then(r => r.json()),
        apiGet("/api/inventory/categories", token).then(r => r.json()),
        apiGet("/api/inventory/suppliers", token).then(r => r.json()),
      ]);
      setProducts(Array.isArray(pr) ? pr : []);
      setCategories(Array.isArray(cr) ? cr : []);
      setSuppliers(Array.isArray(sr) ? sr : []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openDetail = (id) => { setSelectedId(id); setView("detail"); };
  const backToList = () => { setView("list"); setSelectedId(null); };

  const handleSaved = (data, isNew) => {
    if (isNew) {
      setProducts(prev => [data, ...prev]);
    } else {
      setProducts(prev => prev.map(p => p.id === data.id ? data : p));
    }
    backToList();
  };

  const handleDeleted = (id) => {
    setProducts(prev => prev.filter(p => p.id !== id));
    backToList();
  };

  if (view === "detail") {
    return (
      <ProductDetail
        productId={selectedId === "__new__" ? null : selectedId}
        isNew={selectedId === "__new__"}
        categories={categories}
        suppliers={suppliers}
        token={token}
        go={go}
        onBack={backToList}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    );
  }

  return (
    <ProductList
      products={products}
      categories={categories}
      loading={loading}
      role={role}
      token={token}
      onSelect={openDetail}
      onAddNew={() => openDetail("__new__")}
      onImported={fetchAll}
    />
  );
}
